import { ExcelPlanResponse, FilesService, ProductionPlanService } from "@/api";
import { useTaskWithProgress } from "@/api/hooks/useTaskWithProgress";
import ResultWithProgress from "@/components/ResultWithProgress";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "primereact/button";
import ItemMasterSummary from "../components/ItemMasterSummary";
import CheckboxGroup, { CheckboxOption } from "@/components/CheckboxGroup";
import DataDeficiencyDialog from "../components/DataDeficiencyDialog";
import uniq from "lodash-es/uniq";
import { useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { toastAtom } from "@/atoms/toastAtom";


interface LoaderProps {
  onLoad(data: ExcelPlanResponse): void;
}


const Loader : React.FC<LoaderProps> = ({ onLoad }) => {
  const navigate = useNavigate()
  const [ignoreChecks, setIgnoreChecks] = useState<Record<string, boolean>>({})
  const [reloadCount, setReloadCount] = useState(0)
  const [isReloadingSource, setIsReloadingSource] = useState(false)
  const [dataDeficiencyDialogVisible, setDataDeficiencyDialogVisible] = useState(false)
  const toast = useAtomValue(toastAtom)
  const {
    isLoading,
    isFetched,
    progress,
    lastLog,
    logs,
    isError,
    error,
    data: createPlanResult,
    refetch
  } = useTaskWithProgress(
    ()=>ProductionPlanService.createProductionPlan(reloadCount > 0 ? 1: 0),
    ['createPlan',reloadCount],
    {}
  )




  const ignoreCheckOptions: CheckboxOption[] = useMemo(() => {
    const options: CheckboxOption[] = uniq(
      createPlanResult?.itemMaster.map(m => m.廃番)
    )
      .filter(Boolean)
      .map(id => ({
        id,
      }))
    //default true
    const disconEntry = options.find(m => m.id === '廃番')
    if (disconEntry) {
      disconEntry.defaultChecked = true
    }
    options.push({
      id: 'データ不備',
      tooltip: '在庫上限、下限、工程時間いずれかが設定されていない品目',
    })
    options.push({
      id: '生産計画0',
      tooltip: '次月生産計画数が0の品目',
    })
    return options
  }, [createPlanResult])

  const filteredItems = useMemo(() => {
    const ignoreDiscons = Object.keys(ignoreChecks).filter(k => ignoreChecks[k] && k !== 'データ不備')
    const ignoreZeroProduction = ignoreChecks['生産計画0']

    return (
      createPlanResult?.itemMaster.filter(
      item => !ignoreDiscons.includes(item.廃番)
        && !((Array.isArray(item.データ不備) && item.データ不備.length > 0) && ignoreChecks['データ不備'])
        && !(ignoreZeroProduction && item.次月生産計画.value === 0)
      ) ?? []
    )
  }, [createPlanResult,ignoreChecks])

  // データ不備の品目数を計算
  const dataDeficiencyCount = useMemo(() => {
    return createPlanResult?.itemMaster.filter(item => Array.isArray(item.データ不備) && item.データ不備.length > 0).length ?? 0
  }, [createPlanResult?.itemMaster])

  const handleProceed = () => {
    if(isError || !createPlanResult){
      return
    }
    onLoad({
      ...createPlanResult,
      itemMaster: filteredItems,
    } satisfies ExcelPlanResponse)
  }


  const handleReload = async () => {
    const onedriveFileId = createPlanResult?.fileMeta?.onedrive_file_id

    if (!onedriveFileId) {
      setReloadCount(prev => prev + 1)
      return
    }

    try {
      setIsReloadingSource(true)
      await FilesService.downloadOnedriveFile({ file_id: onedriveFileId })
      setReloadCount(prev => prev + 1)
    } catch (err) {
      console.error('OneDrive reload failed', err)
      toast?.show({
        severity: 'error',
        summary: '再読込エラー',
        detail: 'OneDriveからファイルを再取得できませんでした。',
        life: 4000,
      })
    } finally {
      setIsReloadingSource(false)
    }
  }

  useEffect(() => {
    if (reloadCount > 0) {
      refetch()
    }
  }, [reloadCount, refetch])

  return (
    <div className="mt-6">
      <ResultWithProgress
        isObserving={isLoading}
        progress={progress}
        lastLog={lastLog}
        logs={logs}
        isError={isError}
        error={error}>
          <ItemMasterSummary itemMaster={filteredItems ??  []} />
          <h3>除外品目</h3>
          <CheckboxGroup
            options={ignoreCheckOptions}
            onChange={e => setIgnoreChecks(e)}
          />
          {/* データ不備品目表示ボタン */}
          {dataDeficiencyCount > 0 && (
            <div className="mt-3 mb-3">
              <Button
                label={`データ不備品目を確認 (${dataDeficiencyCount}件)`}
                icon="pi pi-exclamation-triangle"
                severity="warning"
                outlined
                size="small"
                onClick={() => setDataDeficiencyDialogVisible(true)}
                className="w-full"
              />
            </div>
          )}
          <p className="mx-auto text-center mt-4">
            Excelファイルを読み込みました。除外する品目があればチェックを入れて進めてください。
          </p>
        </ResultWithProgress>
      <div className="flex flex-row justify-content-between mt-6">
        <Button label="戻る" icon="pi pi-arrow-left"
          iconPos="left" size="small" severity='secondary'
          onClick={() => { navigate({
            to: "/dashboard",
          })}} />
          {isFetched && (
            (createPlanResult?.fileMeta?.onedrive_file_id && (
              <Button
                label="再読込"
                icon="pi pi-sync"
                severity="warning"
                loading={isLoading || isReloadingSource}
                disabled={isLoading || isReloadingSource}
                onClick={handleReload}
              />
            ))
          )}
        <Button label="進む" icon="pi pi-arrow-right"
          iconPos="right" size="small" severity='success'
          disabled={isLoading || isError || filteredItems.length === 0}
          onClick={handleProceed} />
      </div>

      {/* データ不備品目ダイアログ */}
      <DataDeficiencyDialog
        visible={dataDeficiencyDialogVisible}
        onHide={() => setDataDeficiencyDialogVisible(false)}
        items={createPlanResult?.itemMaster ?? []}
      />
    </div>
  )
}
export default Loader
